import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../../api/client";
import { useAuth } from "../../../context/AuthContext";
import { useRealtime } from "../../../realtime/useRealtime";
import { Btn, OrderCard, StatusBadge } from "../../../components/AdminUI";

export default function OperatorDashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [presence, setPresence] = useState<"online" | "offline">("online");
  const [active, setActive] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    adminApi.myOrders().then((res) => {
      setOrders(res.orders);
      setActive((cur: any) => {
        if (cur) {
          const stillThere = res.orders.find((o) => o.id === cur.id);
          if (stillThere) return stillThere;
        }
        return res.orders.find((o) => URGENT.includes(o.status)) ?? res.orders[0] ?? null;
      });
    });
  }, []);

  useEffect(() => {
    adminApi.setPresence("online");
    refresh();
    const onUnload = () => adminApi.setPresence("offline");
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [refresh]);

  useRealtime(user ? `operator:${user.id}` : null, (event) => {
    if (event.type === "order.updated" || event.type === "order.created") refresh();
  });

  async function togglePresence() {
    const next = presence === "online" ? "offline" : "online";
    await adminApi.setPresence(next);
    setPresence(next);
  }

  async function takeNext() {
    setBusy(true);
    try {
      const res = await adminApi.nextOrder();
      if (res.order) {
        setActive(res.order);
        refresh();
      }
    } finally {
      setBusy(false);
    }
  }

  async function act(fn: () => Promise<{ order: any }>) {
    setBusy(true);
    try {
      const res = await fn();
      setActive(res.order);
      refresh();
    } finally {
      setBusy(false);
    }
  }

  const urgentCount = orders.filter((o) => URGENT.includes(o.status)).length;
  const doneToday = orders.filter((o) => o.status === "COMPLETED").length;

  return (
    <div className="min-h-screen bg-gm-bg text-gm-text">
      <header className="flex items-center justify-between border-b border-gm-border px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="text-xl font-extrabold">GEO <span className="text-gm-gold">MASTER</span></span>
          <span className="text-xs text-gm-muted">Панель администратора</span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={togglePresence} className="flex items-center gap-2 text-sm font-semibold">
            <span className={`h-2 w-2 rounded-full ${presence === "online" ? "bg-gm-success" : "bg-gm-muted"}`} />
            {presence === "online" ? "Онлайн" : "Оффлайн"}
          </button>
          <span className="text-sm">{user?.name} · Оператор</span>
          <button onClick={() => logout()} className="text-sm text-gm-muted hover:text-gm-text">Выйти</button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MiniStat label="Срочные задачи" value={urgentCount} tone="danger" />
          <MiniStat label="Мои активные" value={orders.length} />
          <MiniStat label="Завершено сегодня" value={doneToday} tone="success" />
          <MiniStat label="Заказ в работе" value={active ? `#${active.shortId}` : "—"} />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr,320px]">
          <div className="rounded-2xl border border-gm-border bg-gm-panel p-5">
            {!active ? (
              <div className="py-16 text-center text-gm-muted">
                Нет активной задачи.
                <div className="mt-4"><Btn onClick={takeNext} disabled={busy}>ВЗЯТЬ СЛЕДУЮЩИЙ ЗАКАЗ →</Btn></div>
              </div>
            ) : (
              <ActiveOrderPanel order={active} busy={busy} act={act} onTakeNext={takeNext} />
            )}
          </div>

          <div className="rounded-2xl border border-gm-border bg-gm-panel p-4">
            <div className="mb-3 text-sm font-bold text-gm-muted">Мои заказы ({orders.length})</div>
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} onClick={() => setActive(o)} className="cursor-pointer">
                  <OrderCard order={o} />
                </div>
              ))}
              {orders.length === 0 && <div className="text-sm text-gm-muted">Пока пусто</div>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const URGENT = ["PAYMENT_REVIEW", "CODE_REVIEW"];

function MiniStat({ label, value, tone }: { label: string; value: string | number; tone?: "danger" | "success" }) {
  const color = tone === "danger" ? "text-gm-danger" : tone === "success" ? "text-gm-success" : "text-gm-text";
  return (
    <div className="rounded-xl border border-gm-border bg-gm-panel p-4">
      <div className="text-xs text-gm-muted">{label}</div>
      <div className={`mt-1 text-2xl font-extrabold ${color}`}>{value}</div>
    </div>
  );
}

function ActiveOrderPanel({ order, busy, act, onTakeNext }: { order: any; busy: boolean; act: (fn: () => Promise<{ order: any }>) => void; onTakeNext: () => void }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-lg font-bold text-gm-gold">Заказ #{order.shortId}</div>
          <div className="text-sm text-gm-muted">{order.packageGbSnapshot} ГБ · {order.packagePriceSnapshot} ₸ · {order.phone}</div>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {order.status === "CODE_REVIEW" && <CodeDisplay key={order.updatedAt} orderId={order.id} />}

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {order.status === "PAYMENT_REVIEW" && (
          <>
            <Btn tone="success" disabled={busy} onClick={() => act(() => adminApi.confirmPayment(order.id))}>ПОДТВЕРДИТЬ</Btn>
            <Btn tone="danger" disabled={busy} onClick={() => act(() => adminApi.paymentNotFound(order.id))}>НЕ НАЙДЕНА</Btn>
          </>
        )}
        {order.status === "PAYMENT_CONFIRMED" && (
          <Btn disabled={busy} onClick={() => act(() => adminApi.initiateCode(order.id))}>Я ИНИЦИИРОВАЛ ВО ВНЕШНЕМ ТЕРМИНАЛЕ →</Btn>
        )}
        {order.status === "CODE_REVIEW" && (
          <>
            <Btn tone="success" disabled={busy} onClick={() => act(() => adminApi.codeResult(order.id, "valid"))}>ВЕРНЫЙ</Btn>
            <Btn tone="danger" disabled={busy} onClick={() => act(() => adminApi.codeResult(order.id, "invalid"))}>НЕВЕРНЫЙ</Btn>
            <Btn tone="muted" disabled={busy} onClick={() => act(() => adminApi.codeResult(order.id, "impossible"))}>АКТИВАЦИЯ НЕВОЗМОЖНА</Btn>
          </>
        )}
        {order.status === "INVALID_CODE" && (
          <Btn disabled={busy} onClick={() => act(() => adminApi.codeResend(order.id))}>ЗАПРОСИТЬ НОВЫЙ КОД</Btn>
        )}
        {order.status === "REFUND_PENDING" && (
          <Btn disabled={busy} onClick={() => act(() => adminApi.refundProcess(order.id))}>НАЧАТЬ ВОЗВРАТ</Btn>
        )}
        {order.status === "REFUND_PROCESSING" && (
          <Btn tone="success" disabled={busy} onClick={() => act(() => adminApi.refundComplete(order.id))}>ВОЗВРАТ ВЫПОЛНЕН</Btn>
        )}
        {["AWAITING_PAYMENT", "AWAITING_CODE", "CODE_SUBMITTED", "COMPLETED"].includes(order.status) && (
          <div className="text-sm text-gm-muted sm:col-span-2">Ожидаем действия клиента — realtime уведомит автоматически.</div>
        )}
      </div>

      <div className="mt-4 flex gap-3">
        <Btn tone="muted" disabled={busy} onClick={() => act(() => adminApi.escalate(order.id))}>ПЕРЕДАТЬ ВЛАДЕЛЬЦУ</Btn>
        <Btn tone="muted" disabled={busy} onClick={onTakeNext}>СЛЕДУЮЩАЯ ЗАДАЧА →</Btn>
      </div>
    </div>
  );
}
/** Показывает код, который ввёл клиент, — чтобы оператор мог вручную ввести его во внешний терминал. */
function CodeDisplay({ orderId }: { orderId: string }) {
  const [data, setData] = useState<{ code: string; attempts: number; maxAttempts: number } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    adminApi.getCode(orderId).then(setData).catch(() => setError(true));
  }, [orderId]);

  if (error) return <div className="mt-4 text-sm text-gm-danger">Не удалось загрузить код. Обновите страницу.</div>;
  if (!data) return <div className="mt-4 text-sm text-gm-muted">Загружаем код…</div>;

  return (
    <div className="mt-4 rounded-xl border border-gm-gold/40 bg-gm-gold/10 p-4 text-center">
      <div className="text-xs font-semibold text-gm-muted">Код, введённый клиентом</div>
      <div className="mt-1 font-mono text-3xl font-extrabold tracking-[0.3em] text-gm-gold">{data.code}</div>
      <div className="mt-1 text-xs text-gm-muted">Попытка {data.attempts + 1} из {data.maxAttempts}</div>
    </div>
  );
}